import qs from "qs";
import axios, { type AxiosRequestConfig } from "axios";
import { getSagApiUrl } from "@/api/http-client-helpers";
import { addXBtmidHeaderInterceptor } from "@/api/interceptors/x-btmid-header-interceptor";
import { addIfModifiedSinceHeaderInterceptor } from "@/api/interceptors/if-modified-since-header-interceptor";
import { addXPlayerIdHeaderInterceptor } from "@/api/interceptors/x-player-id-header-interceptor";
import { addSagAuthInterceptor } from "@/api/interceptors/sag-auth-interceptor";
import { addAuthorizationHeaderInterceptor } from "@/api/interceptors/authorization-header-interceptor";
import { ngLogoutUnauthorized, refreshTokens } from "@/shared/stores/use-auth-store";
import { logException } from "@/shared/utils/logger/use-app-logger";
import axiosRetry from "axios-retry"; // Import axios-retry

/**
 * Axios basic configuration
 * Some general configuration can be added like timeout, headers, params etc. More details can be found on https://github.com/axios/axios
 * */
const config: AxiosRequestConfig = {
    baseURL: getSagApiUrl("openbet", false),
    headers: {
        "X-Accept-Language": "cs-CZ",
        "Cache-Control": "public, max-age=5",
        "x-ob-channel": ngLoader.isMobile() ? "M" : "I",
    },
    responseType: "json",
    paramsSerializer: function (params) {
        return qs.stringify(params, { arrayFormat: "comma" }); // Separate array elements with comma during serialization
    },
};

const createInstance = (secured: boolean = false) => {
    /**
     * Creating the instance of Axios
     * It is because, in large scale application we may need to consume APIs from more than single server,
     * So, may need to create multiple http client with different config
     * Only this client will be used rather than axios in the application
     **/
    const httpClient = axios.create(config);

    /** Adding the request/response interceptors */
    addXBtmidHeaderInterceptor(httpClient);
    addIfModifiedSinceHeaderInterceptor(httpClient);
    addSagAuthInterceptor(httpClient);
    addXPlayerIdHeaderInterceptor(httpClient);
    if (secured) {
        addAuthorizationHeaderInterceptor(httpClient);
        axiosRetry(httpClient, {
            retries: 3,
            retryCondition: (error) => {
                return error.response?.status === 401;
            },
        });
    }

    httpClient.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error) => {
            if (secured && error.response?.status === 401) {
                try {
                    await refreshTokens();
                    return httpClient.request(error.config);
                } catch (refreshError) {
                    //logout if refresh fails
                    ngLogoutUnauthorized();
                }
            }
            logException("openbet-http-client", error, {}, true);
            return Promise.reject(error);
        },
    );
    return httpClient;
};

const httpClient = createInstance();
const securedHttpClient = createInstance(true);

export { httpClient as openbetHttpClient, securedHttpClient as securedOpenbetHttpClient };
